import { Fragment, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import Chart from "chart.js/auto";
import useToken from "./useToken";

const DEFAULT_COMPANIES = [
  { _id: "bnp", name: "BNP Paribas", logo_url: "" },
  { _id: "action logement", name: "Action Logement", logo_url: "" },
  { _id: "hydros", name: "Hydros Consulting", logo_url: "" },
  { _id: "Ville de Paris", name: "Ville de Paris", logo_url: "" },
];
const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

function toISO(date) {
  return date.toISOString().split("T")[0];
}

function formatDate(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function daysRemaining(endDateISO) {
  const end = new Date(endDateISO);
  end.setHours(23, 59, 59);
  const diff = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

function getDateRange(mode, customStart, customEnd) {
  const today = new Date();
  if (mode === "12months") {
    const start = new Date(today);
    start.setMonth(start.getMonth() - 11);
    start.setDate(1);
    return { start: toISO(start), end: toISO(today) };
  }
  if (mode === "year") {
    return { start: `${today.getFullYear()}-01-01`, end: toISO(today) };
  }
  if (mode === "custom" || mode === "licence") {
    return { start: customStart, end: customEnd };
  }
  return { start: null, end: null };
}

function buildMonthlyChartData(monthlyWorkshops, mode, customStart, customEnd) {
  const allKeys = Object.keys(monthlyWorkshops).sort();
  if (mode === "total") {
    if (allKeys.length === 0) return { labels: [], values: [] };
    return {
      labels: allKeys.map((m) => {
        const [year, month] = m.split("-");
        return `${MONTH_LABELS[parseInt(month) - 1]} ${year}`;
      }),
      values: allKeys.map((m) => monthlyWorkshops[m]),
    };
  }
  const { start, end } = getDateRange(mode, customStart, customEnd);
  if (!start || !end) return { labels: [], values: [] };
  const months = [];
  const cursor = new Date(new Date(start).getFullYear(), new Date(start).getMonth(), 1);
  const endDate = new Date(end);
  while (cursor <= endDate) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return {
    labels: months.map((m) => {
      const [year, month] = m.split("-");
      return `${MONTH_LABELS[parseInt(month) - 1]} ${year}`;
    }),
    values: months.map((m) => monthlyWorkshops[m] || 0),
  };
}

function toCompanyId(name) {
  return name
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

export function CreateCompanyModal({ token, onClose, onSaved }) {
  const [name, setName] = useState("");
  const id = toCompanyId(name);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload_image`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.image_url) setLogoUrl(data.image_url);
    } catch (e) {
      setError("Erreur lors de l'upload du logo.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) uploadFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/company_create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ _id: id, name, logo_url: logoUrl }),
      });
      const data = await res.json();
      if (data.success) { onSaved(id); onClose(); }
      else setError(data.error || "Erreur lors de la création.");
    } catch (e) {
      setError("Erreur réseau.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup">
      <div className="popup-content" style={{ width: 440 }}>
        <h3>Nouvelle entreprise</h3>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, textAlign: "left" }}>
          <div className="form-group">
            <label>Nom de l'entreprise</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="ex: BNP Paribas" />
            {name && (
              <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Identifiant : <strong>{id}</strong>
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Logo</label>
            <div
              className={`logo-dropzone ${dragging ? "dragging" : ""} ${logoUrl ? "has-logo" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("logo-file-input").click()}
            >
              {uploading ? (
                <span>Upload en cours...</span>
              ) : logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain" }} />
              ) : (
                <span>Glisser une image ici ou cliquer</span>
              )}
            </div>
            <input id="logo-file-input" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileInput} />
          </div>
          {error && <p style={{ color: "#c72f30", margin: 0, fontSize: 13 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
            <button type="button" className="orange-button popup-cancel-button" style={{ backgroundColor: "var(--hydros-blue)" }} onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="orange-button popup-submit-button" disabled={loading || uploading}>
              {loading ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const EMPTY_LICENCE = {
  start_date: "", end_date: "", workshops_included: "", cost: "", notes: "",
};

function AddLicenceModal({ company, token, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_LICENCE);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/company_add_licence/${encodeURIComponent(company._id)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        }
      );
      const data = await res.json();
      if (data.success) { onSaved(); onClose(); }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="popup">
      <div className="popup-content" style={{ width: 480 }}>
        <h3>Nouvelle licence — {company.name}</h3>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12, textAlign: "left" }}>
          <div className="licence-form-row">
            <div className="form-group">
              <label>Date de début</label>
              <input type="date" value={form.start_date} onChange={set("start_date")} required />
            </div>
            <div className="form-group">
              <label>Date de fin</label>
              <input type="date" value={form.end_date} onChange={set("end_date")} required />
            </div>
          </div>
          <div className="licence-form-row">
            <div className="form-group">
              <label>Ateliers inclus</label>
              <input type="number" min="1" value={form.workshops_included} onChange={set("workshops_included")} required />
            </div>
            <div className="form-group">
              <label>Tarif (€)</label>
              <input type="number" min="0" value={form.cost} onChange={set("cost")} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input type="text" value={form.notes} onChange={set("notes")} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
            <button type="button" className="orange-button popup-cancel-button" style={{ backgroundColor: "var(--hydros-blue)" }} onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="orange-button popup-submit-button" disabled={loading}>
              {loading ? "Enregistrement..." : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompaniesOverview({ token, onSelect }) {
  const [overview, setOverview] = useState(null);
  const [modalCompany, setModalCompany] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadOverview = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/companies_overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadOverview(); }, [token]);

  if (!overview) return (
    <div className="loader-container">
      <img src="/loader.gif" className="loader" alt="Loading..." />
    </div>
  );

  return (
    <>
    <div className="overview-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 className="blue-title" style={{ margin: 0 }}>Vue d'ensemble</h2>
        <button className="orange-button add-anim-button" onClick={() => setShowCreateModal(true)}>
          + Nouvelle entreprise
        </button>
      </div>
      <div className="table-container">
        <table className="animators-table overview-table">
          <thead>
            <tr>
              <th>Entreprise</th>
              <th>Animateurs</th>
              <th>Ateliers réalisés</th>
              <th>Ateliers restants</th>
              <th>Jours restants</th>
              <th>Fin de licence</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {overview.map((company) => {
              const lic = company.active_licence;
              const daysLeft = lic?.days_remaining;
              const expired = daysLeft !== null && daysLeft < 0;
              const almostOver = !expired && daysLeft !== null && daysLeft <= 45;
              const critical = !expired && daysLeft !== null && daysLeft < 15;
              const workshopsOver = lic && lic.workshops_remaining < 0;
              const workshopsCritical = lic && !workshopsOver && lic.workshops_remaining < 2;
              const workshopsLow = lic && !workshopsOver && !workshopsCritical && lic.workshops_remaining < 5;

              return (
                <tr
                  key={company._id}
                  onClick={() => onSelect(company._id)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ textAlign: "center" }}>
                    {company.logo_url
                      ? <img src={company.logo_url} alt={company.name} style={{ height: 28, objectFit: "contain" }} />
                      : <span style={{ fontWeight: 600 }}>{company.name}</span>}
                  </td>
                  <td>{company.number_animators}</td>
                  <td>
                    {lic
                      ? `${company.number_done_workshop} / ${lic.workshops_included}`
                      : company.number_done_workshop}
                  </td>
                  <td>
                    {lic ? (
                      <span className={`overview-badge ${workshopsOver || workshopsCritical ? "badge-danger" : workshopsLow ? "badge-warning" : "badge-ok"}`}>
                        {lic.workshops_remaining}
                      </span>
                    ) : "—"}
                  </td>
                  <td>
                    {lic && daysLeft !== null ? (
                      <span className={`overview-badge ${expired || critical ? "badge-danger" : almostOver ? "badge-warning" : "badge-ok"}`}>
                        {expired ? "Expirée" : `${daysLeft} j`}
                      </span>
                    ) : "—"}
                  </td>
                  <td>{lic?.end_date ? formatDate(lic.end_date) : "—"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="add-licence-btn"
                      title="Ajouter une licence"
                      onClick={() => setModalCompany(company)}
                    >
                      + Licence
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    {modalCompany && (
      <AddLicenceModal
        company={modalCompany}
        token={token}
        onClose={() => setModalCompany(null)}
        onSaved={loadOverview}
      />
    )}
    {showCreateModal && (
      <CreateCompanyModal
        token={token}
        onClose={() => setShowCreateModal(false)}
        onSaved={loadOverview}
      />
    )}
    </>
  );
}

const PODIUM_MEDALS = ["🥇", "🥈", "🥉"];
const PODIUM_COLORS = ["#c9a82c", "#9e9e9e", "#a0522d"];
const PODIUM_HEIGHTS = [130, 90, 70];
const PODIUM_ORDER = [1, 0, 2];

function Podium({ animators, isPdfExporting }) {
  const top3 = [...animators].sort((a, b) => b.workshops_done - a.workshops_done).slice(0, 3);
  if (top3.length === 0) return null;
  const ordered = PODIUM_ORDER.map((i) => top3[i]).filter(Boolean);
  return (
    <div className="podium-section">
      <h3 className={isPdfExporting ? "gs-section-title" : "blue-title"} style={{ marginBottom: isPdfExporting ? 16 : 20 }}>Top animateurs</h3>
      <div className="podium-wrapper">
        {ordered.map((animator, displayIdx) => {
          const rank = PODIUM_ORDER[displayIdx];
          return (
            <div key={animator._id} className="podium-column">
              <div className="podium-name">{animator.firstname} {animator.name}</div>
              <div className="podium-workshops">{animator.workshops_done} atelier{animator.workshops_done > 1 ? "s" : ""}</div>
              <div className="podium-block" style={{ height: PODIUM_HEIGHTS[rank], backgroundColor: PODIUM_COLORS[rank] }}>
                <span className="podium-medal">{PODIUM_MEDALS[rank]}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LicenceIndicators({ licence, workshopsDone }) {
  const remaining = licence.workshops_included - workshopsDone;
  const days = daysRemaining(licence.end_date);
  const pct = Math.min(100, Math.round((workshopsDone / licence.workshops_included) * 100));
  const expired = days < 0;
  const almostOver = !expired && days <= 30;
  const overused = remaining < 0;

  return (
    <div className="licence-indicators">
      <div className="licence-period-label">
        Licence du {formatDate(licence.start_date)} au {formatDate(licence.end_date)}
        {licence.notes ? <span className="licence-notes"> — {licence.notes}</span> : null}
      </div>

      <div className="licence-kpis">
        <div className={`licence-kpi ${overused ? "kpi-danger" : remaining <= 2 ? "kpi-warning" : "kpi-ok"}`}>
          <div className="licence-kpi-value">{remaining}</div>
          <div className="licence-kpi-label">Atelier{Math.abs(remaining) > 1 ? "s" : ""} restant{remaining < 0 ? "s (dépassé)" : "s"}</div>
          <div className="licence-progress-bar">
            <div
              className="licence-progress-fill"
              style={{ width: `${pct}%`, backgroundColor: overused ? "#c72f30" : pct > 80 ? "#db6c52" : "#205673" }}
            />
          </div>
          <div className="licence-kpi-sub">{workshopsDone} / {licence.workshops_included} utilisés</div>
        </div>

        <div className={`licence-kpi ${expired ? "kpi-danger" : almostOver ? "kpi-warning" : "kpi-ok"}`}>
          <div className="licence-kpi-value">{expired ? "Expirée" : days}</div>
          <div className="licence-kpi-label">{expired ? "Licence expirée" : "Jour" + (days > 1 ? "s" : "") + " restant" + (days > 1 ? "s" : "")}</div>
          <div className="licence-kpi-sub">Échéance : {formatDate(licence.end_date)}</div>
        </div>

        <div className="licence-kpi kpi-neutral">
          <div className="licence-kpi-value">{licence.cost > 0 ? `${licence.cost.toLocaleString("fr-FR")} €` : "—"}</div>
          <div className="licence-kpi-label">Tarif</div>
          <div className="licence-kpi-sub">{licence.payment_status}</div>
        </div>
      </div>
    </div>
  );
}

const FILTERS = [
  { mode: "total", label: "Toute la période" },
  { mode: "12months", label: "12 derniers mois" },
  { mode: "year", label: "Année en cours" },
  { mode: "custom", label: "Période personnalisée" },
];

function CompanyDashboard() {
  const { token } = useToken();
  const navigate = useNavigate();
  const pdfRef = useRef(null);
  const doughnutRef = useRef(null);
  const barRef = useRef(null);
  const [doughnutImgUrl, setDoughnutImgUrl] = useState(null);
  const [barImgUrl, setBarImgUrl] = useState(null);
  const [companies, setCompanies] = useState(DEFAULT_COMPANIES);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedLicenceIdx, setSelectedLicenceIdx] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [filterMode, setFilterMode] = useState("12months");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "workshops_done", direction: "desc" });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/get_companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data && data.length > 0) setCompanies(data);
      } catch (e) {
        console.error("Error fetching companies:", e);
      }
    };
    fetchCompanies();
  }, [token]);

  const fetchCompanyInfo = async (company) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/company_info/${encodeURIComponent(company)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCompanyInfo(data);
        setSelectedLicenceIdx(null);
      } else {
        setCompanyInfo(null);
      }
    } catch (e) {
      console.error("Error fetching company info:", e);
      setCompanyInfo(null);
    }
  };

  const fetchStats = async (company, mode, cStart, cEnd) => {
    if (!company) return;
    if (mode === "custom" && (!cStart || !cEnd)) return;
    if (mode === "licence" && (!cStart || !cEnd)) return;
    setLoading(true);
    setStats(null);
    try {
      const { start, end } = getDateRange(mode, cStart, cEnd);
      const params = new URLSearchParams();
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
      const query = params.toString() ? `?${params}` : "";
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/company_dashboard/${encodeURIComponent(company)}${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error("Error fetching company stats:", e);
    } finally {
      setLoading(false);
    }
  };

  const selectCompany = (companyId) => {
    setSelectedCompany(companyId);
    setCompanyInfo(null);
    setSelectedLicenceIdx(null);
    setStats(null);
    setFilterMode("12months");
    setCustomStart("");
    setCustomEnd("");
    fetchCompanyInfo(companyId);
    fetchStats(companyId, "12months", "", "");
  };

  const handleBack = () => {
    setSelectedCompany("");
    setCompanyInfo(null);
    setSelectedLicenceIdx(null);
    setStats(null);
  };

  const handleFilterMode = (mode) => {
    setFilterMode(mode);
    setSelectedLicenceIdx(null);
    if (mode !== "custom") fetchStats(selectedCompany, mode, customStart, customEnd);
  };

  const handleCustomApply = () => {
    setSelectedLicenceIdx(null);
    fetchStats(selectedCompany, "custom", customStart, customEnd);
  };

  const handleLicenceSelect = (e) => {
    const idx = e.target.value === "" ? null : parseInt(e.target.value);
    setSelectedLicenceIdx(idx);
    if (idx === null) return;
    const licence = companyInfo.licences[idx];
    const start = licence.start_date.split("T")[0];
    const end = licence.end_date.split("T")[0];
    setFilterMode("licence");
    setCustomStart(start);
    setCustomEnd(end);
    fetchStats(selectedCompany, "licence", start, end);
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedAnimators = useMemo(() => {
    if (!stats?.animators) return [];
    if (!sortConfig.key) return stats.animators;
    return [...stats.animators].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      const cmp = typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
  }, [stats, sortConfig]);

  const CONFIG_COLORS = [
    "rgba(32, 86, 115, 0.85)",
    "rgba(219, 108, 82, 0.85)",
    "rgba(76, 175, 80, 0.85)",
    "rgba(245, 200, 66, 0.85)",
    "rgba(156, 39, 176, 0.85)",
    "rgba(183, 224, 225, 0.85)",
  ];

  const configNames = useMemo(() => {
    if (!stats?.workshops_by_config) return [];
    return Object.entries(stats.workshops_by_config)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [stats]);

  const chartData = useMemo(() => {
    if (!stats || configNames.length === 0) return null;
    const { labels } = buildMonthlyChartData(
      stats.monthly_workshops || {},
      filterMode,
      customStart,
      customEnd
    );
    if (labels.length === 0) return null;

    const byConfig = stats.monthly_workshops_by_config || {};
    const monthKeys = Object.keys(stats.monthly_workshops || {}).sort();
    const { start } = getDateRange(filterMode, customStart, customEnd);
    const orderedKeys = filterMode === "total"
      ? monthKeys
      : labels.map((_, i) => {
          const cursor = new Date(new Date(start).getFullYear(), new Date(start).getMonth() + i, 1);
          return `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
        });

    const datasets = configNames.map((name, i) => {
      const data = orderedKeys.map((k) => byConfig[k]?.[name] || 0);
      return {
        label: name,
        data,
        backgroundColor: CONFIG_COLORS[i % CONFIG_COLORS.length],
        borderRadius: data.map((v, colIdx) => {
          if (v === 0) return 0;
          const isTop = configNames.slice(i + 1).every(
            (later) => (byConfig[orderedKeys[colIdx]]?.[later] || 0) === 0
          );
          return isTop ? { topLeft: 6, topRight: 6 } : 0;
        }),
        borderSkipped: false,
        borderColor: "rgba(255,255,255,0.6)",
        borderWidth: { top: 0, left: 0, right: 0, bottom: configNames.length > 1 ? 2 : 0 },
        stack: "workshops",
      };
    });

    return { labels, datasets };
  }, [stats, configNames, filterMode, customStart, customEnd]);

  const chartOptions = {
    animation: false,
    responsive: true,
    devicePixelRatio: 1,
    layout: { padding: { top: 16, right: 8, bottom: 0, left: 0 } },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "rectRounded",
          font: { size: 12, weight: "500" },
          color: "#205673",
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      datalabels: false,
      tooltip: {
        backgroundColor: "rgba(20, 60, 90, 0.95)",
        titleColor: "#fff",
        titleFont: { size: 13, weight: "600" },
        bodyColor: "rgba(255,255,255,0.85)",
        bodyFont: { size: 12 },
        cornerRadius: 10,
        padding: 12,
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        callbacks: {
          title: (items) => items[0].label,
          label: (ctx) => {
            const v = ctx.parsed.y;
            return `  ${ctx.dataset.label} : ${v} atelier${v > 1 ? "s" : ""}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: "#205673",
          font: { size: 12, weight: "500" },
          padding: 8,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        border: { display: false, dash: [4, 4] },
        ticks: {
          stepSize: 1,
          precision: 0,
          color: "#94a3b8",
          font: { size: 11 },
          padding: 8,
        },
        grid: {
          color: "rgba(32, 86, 115, 0.07)",
          lineWidth: 1,
        },
      },
    },
  };

  const activeLicence =
    selectedLicenceIdx !== null && companyInfo?.licences
      ? companyInfo.licences[selectedLicenceIdx]
      : null;

  const handleExportPDF = async () => {
    if (doughnutRef.current) {
      setDoughnutImgUrl(doughnutRef.current.toBase64Image("image/png", 1));
    }
    if (barRef.current) {
      setBarImgUrl(barRef.current.toBase64Image("image/png", 1));
    }
    setIsPdfExporting(true);
    await new Promise((r) => setTimeout(r, 300));
    const html2pdf = (await import("html2pdf.js")).default;
    const element = pdfRef.current;
    const companyName = companyInfo?.name || selectedCompany || "dashboard";
    await html2pdf().set({
      margin: [8, 8, 8, 8],
      filename: `dashboard_${companyName.replace(/\s+/g, "_")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, scrollY: 0 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      pagebreak: { before: ".pdf-page-break" },
    }).from(element).save();
    setIsPdfExporting(false);
    setDoughnutImgUrl(null);
    setBarImgUrl(null);
  };

  return (
    <div className="company-dashboard-page">
      <button className="cd-back-btn" onClick={() => navigate("/animators_page")}>
        ← Liste des animateurs
      </button>
      <div className="company-dashboard-header">
        <h1 className="blue-title" style={{ fontSize: "28px", margin: 0 }}>
          Dashboard Entreprise
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {selectedCompany && (
            <>
              <select
                className="company-select"
                value={selectedCompany}
                onChange={(e) => e.target.value && selectCompany(e.target.value)}
              >
                {companies.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              <button className="date-filter-btn" onClick={handleExportPDF}>
                ⬇ Exporter PDF
              </button>
              <button className="date-filter-btn" onClick={handleBack}>
                ← Vue générale
              </button>
            </>
          )}
        </div>
      </div>

      <div ref={pdfRef}>
      {companyInfo?.logo_url && (
        <div className="company-hero">
          <img src={companyInfo.logo_url} alt={companyInfo.name} className="company-hero-logo" />
        </div>
      )}

      {companyInfo?.licences?.length > 0 && (
        <div className="licence-selector-bar">
          <span className="licence-selector-label">Licence :</span>
          <select className="company-select" value={selectedLicenceIdx ?? ""} onChange={handleLicenceSelect}>
            <option value="">— Sélectionner une période de licence —</option>
            {companyInfo.licences.map((l, i) => (
              <option key={i} value={i}>
                {formatDate(l.start_date)} → {formatDate(l.end_date)} ({l.workshops_included} ateliers)
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedCompany && (
        <div className="date-filter-bar">
          {FILTERS.map(({ mode, label }) => (
            <button
              key={mode}
              className={`date-filter-btn ${filterMode === mode ? "active" : ""}`}
              onClick={() => handleFilterMode(mode)}
            >
              {label}
            </button>
          ))}
          {filterMode === "licence" && activeLicence && (
            <span className="licence-active-badge">
              Période de licence active
            </span>
          )}
          {filterMode === "custom" && (
            <div className="custom-date-inputs">
              <input type="date" className="date-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <span style={{ color: "var(--hydros-blue)", fontWeight: 600 }}>→</span>
              <input type="date" className="date-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              <button className="date-apply-btn" onClick={handleCustomApply}>Appliquer</button>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loader-container">
          <img src="/loader.gif" className="loader" alt="Loading..." />
        </div>
      )}

      {!loading && stats && (
        <Fragment>
          {activeLicence && !isPdfExporting && (
            <LicenceIndicators licence={activeLicence} workshopsDone={stats.number_done_workshop} />
          )}

          <div className="dashboard-stats-wrapper company-stats-grid">
            <div className="dashboard-stat-container">
              <div className="dashboard-stat-value">{stats.number_animators}</div>
              <div className="dashboard-stat-header">Animateurs</div>
              <img className="dashboard-stat-logo" src="/reseau.png" alt="Animateurs" />
            </div>
            <div className="dashboard-stat-container">
              <div className="dashboard-stat-value">{stats.number_done_workshop}</div>
              <div className="dashboard-stat-header">Ateliers réalisés</div>
              <img className="dashboard-stat-logo" src="/atelier.png" alt="Ateliers réalisés" />
            </div>
            <div className="dashboard-stat-container">
              <div className="dashboard-stat-value">{stats.number_people_workshop}</div>
              <div className="dashboard-stat-header">Personnes formées</div>
              <img className="dashboard-stat-logo" src="/reseau.png" alt="Personnes formées" />
            </div>
          </div>

          {chartData && (
            <div>
            {isPdfExporting && <div className="pdf-page-break" />}
              <h3 className={isPdfExporting ? "gs-section-title" : "blue-title"} style={{ marginBottom: isPdfExporting ? 16 : 12 }}>
                Ateliers réalisés par mois
              </h3>
              {isPdfExporting && barImgUrl
                ? <img src={barImgUrl} alt="Ateliers réalisés par mois" style={{ width: "100%" }} />
                : <div className="monthly-chart-container monthly-chart-container--large">
                    <Bar ref={barRef} data={chartData} options={chartOptions} />
                  </div>
              }
            </div>
          )}

          {stats.animators?.length > 0 && (
            <div>
            {isPdfExporting && <div className="pdf-page-break" />}
              <Podium animators={stats.animators} isPdfExporting={isPdfExporting} />
            </div>
          )}

          {sortedAnimators.length > 0 && (
            <div className="company-animators-section">
            {isPdfExporting && <div className="pdf-page-break" />}
              <h3 className={isPdfExporting ? "gs-section-title" : "blue-title"} style={{ marginBottom: isPdfExporting ? 16 : 12 }}>
                Animateurs
              </h3>
              <div className="table-container company-animators-table-container" style={isPdfExporting ? { maxHeight: "none", overflow: "visible" } : {}}>
                <table className="animators-table">
                  <thead>
                    <tr>
                      {[
                        { label: "Nom", key: "name" },
                        { label: "Prénom", key: "firstname" },
                        { label: "Ateliers réalisés", key: "workshops_done" },
                        { label: "Personnes formées", key: "participants" },
                      ].map(({ label, key }) => (
                        <th key={key} onClick={() => handleSort(key)} style={{ cursor: "pointer", userSelect: "none" }}>
                          {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "▲" : "▼") : "⇅"}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAnimators.map((a) => (
                      <tr key={a._id}>
                        <td>{a.name}</td>
                        <td>{a.firstname}</td>
                        <td>{a.workshops_done}</td>
                        <td>{a.participants}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {configNames.length > 0 && stats.workshops_by_config && (
            <div>
            {isPdfExporting && <div className="pdf-page-break" />}
              <h3 className={isPdfExporting ? "gs-section-title" : "blue-title"} style={{ marginBottom: isPdfExporting ? 16 : 12 }}>
                Répartition par scénario
              </h3>
              <div className={isPdfExporting ? "" : "monthly-chart-container monthly-chart-container--pie"}>
              <div className="pie-wrapper">
                {isPdfExporting && doughnutImgUrl
                  ? <img src={doughnutImgUrl} alt="Répartition par scénario" style={{ width: "100%" }} />
                  : <Doughnut ref={doughnutRef}
                  data={{
                    labels: configNames,
                    datasets: [{
                      data: configNames.map((k) => stats.workshops_by_config[k] || 0),
                      backgroundColor: configNames.map((_, i) => CONFIG_COLORS[i % CONFIG_COLORS.length]),
                      borderWidth: 3,
                      borderColor: "#fff",
                      hoverOffset: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: { duration: 0 },
                    cutout: "62%",
                    plugins: {
                      datalabels: false,
                      legend: {
                        position: "right",
                        labels: {
                          padding: 16,
                          usePointStyle: true,
                          pointStyle: "circle",
                          font: { size: 13 },
                          color: "#205673",
                          generateLabels: (chart) => {
                            const total = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                            return chart.data.labels.map((label, i) => ({
                              text: `${label}  —  ${chart.data.datasets[0].data[i]} (${Math.round(chart.data.datasets[0].data[i] / total * 100)}%)`,
                              fillStyle: CONFIG_COLORS[i % CONFIG_COLORS.length],
                              strokeStyle: "#fff",
                              lineWidth: 1,
                              index: i,
                            }));
                          },
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(32, 86, 115, 0.92)",
                        titleColor: "#fff",
                        bodyColor: "rgba(255,255,255,0.85)",
                        cornerRadius: 8,
                        padding: 10,
                        callbacks: {
                          label: (ctx) => {
                            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                            const pct = Math.round(ctx.parsed / total * 100);
                            return ` ${ctx.parsed} atelier${ctx.parsed > 1 ? "s" : ""} (${pct}%)`;
                          },
                        },
                      },
                    },
                  }}
                />}
              </div>
            </div>
            </div>
          )}
        </Fragment>
      )}

      </div>
      {!selectedCompany && <CompaniesOverview token={token} onSelect={selectCompany} />}

      {!loading && !stats && selectedCompany && (
        <p style={{ color: "var(--hydros-blue)", marginTop: "40px", textAlign: "center" }}>
          Aucune donnée disponible pour cette entreprise.
        </p>
      )}
    </div>
  );
}

export default CompanyDashboard;
